import posthog from "posthog-js";

import { analyticsEnabled } from "./enabled";
import { ANALYTICS_EVENT, trackEvent } from "./events";
import { registerEnvironmentSuperProperty } from "./register-environment";
import { checkIsPartner, checkIsVerdiStaff } from "./staff-flags";

/**
 * PostHog identity — the only place outside events.ts that touches the SDK's who-is-this side.
 * Call sites say what happened (logged in, org switched); the person/group/super-property split and
 * login dedup live here so they can't be reassembled wrong at each call site. Gated by analyticsEnabled.
 */

/** Descope user details that map to person properties (no distinct id needed to refresh them). */
export type UserProfileDetails = {
    name?: string;
    email?: string;
    roleNames?: string[];
    isAdmin?: boolean;
    adminUsername?: string;
};

/** A user we can identify: profile details plus the Descope distinct id. */
export type AuthenticatedUser = UserProfileDetails & { userId: string };

/** The organization (account) the session is currently acting within. */
export type CurrentOrganization = {
    id: string;
    name?: string;
    adminUseOnly?: boolean;
};

/** Person profile: PII lives here (not on events), and flags are derived once so login and refresh can't disagree. */
function toPersonProperties(details: UserProfileDetails) {
    return {
        name: details.name || undefined,
        email: details.email?.toLowerCase() || undefined,
        is_admin: Boolean(details.isAdmin),
        is_internal: checkIsVerdiStaff({
            email: details.email,
            roleNames: details.roleNames,
            isAdmin: details.isAdmin,
        }),
        is_partner: checkIsPartner(details.roleNames),
        admin_username: details.adminUsername || undefined,
    };
}

/** Link the session to a Descope user and fire USER_LOGIN once — a refresh re-runs this and must not re-fire. */
export function identifyAuthenticatedUser(user: AuthenticatedUser) {
    if (!analyticsEnabled) return;
    const properties = toPersonProperties(user);
    // Anonymous → Descope-id transition is a real login; same id already means an active session.
    const isNewLogin = posthog?.get_distinct_id() !== user.userId;
    posthog?.identify(user.userId, properties);
    posthog?.register({ is_admin: properties.is_admin, is_internal: properties.is_internal });
    if (isNewLogin) {
        trackEvent(ANALYTICS_EVENT.USER_LOGIN, {});
    }
}

/** Refresh the person profile when Descope details arrive late; shares toPersonProperties so flags can't drift. */
export function refreshAuthenticatedUser(details: UserProfileDetails) {
    if (!analyticsEnabled) return;
    posthog?.setPersonProperties(toPersonProperties(details));
}

/** Associate the session with its organization (group) and expose the org id as a super property. */
export function identifyCurrentOrganization(org: CurrentOrganization) {
    if (!analyticsEnabled) return;
    posthog?.group("organization", org.id, {
        name: org.name || undefined,
        admin_use_only: Boolean(org.adminUseOnly),
    });
    posthog?.register({ current_org_id: org.id });
}

/** Clear identity and super properties. Call on logout. */
export function resetAnalytics() {
    if (!analyticsEnabled) return;
    posthog?.reset();
    // reset() clears all super properties; re-attach ambient ones that must survive logout.
    registerEnvironmentSuperProperty();
}
